import React, { useState, useEffect, useCallback } from "react";
import { FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import { fetchEventTaskActivity } from "../../../utils/eventApi";
import { formatDuration } from "../../tasks/taskConstants";
import TimeCell from "../../tasks/components/TimeCell";

/**
 * Which user did what with a task, for this event specifically.
 *
 * `participants` (the Participants & Points tab) answers "how many points did
 * this user earn" per source, summed across the whole event — one row per
 * user. This answers a different question: "who opened this task and never
 * finished it, and who did" — one row per `user_tasks` record, the same
 * `started`/`completed` shape `src/admin/task`'s own Completions tab uses,
 * scoped down to this one event.
 */

const TASK_TYPE_LABEL = {
  website_visit: "Website Visit",
  download_app: "Download App",
  telegram: "Telegram",
  youtube: "YouTube",
};

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "started", label: "Started (not completed)" },
  { key: "completed", label: "Completed" },
];

const TaskActivityTab = ({ eventId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEventTaskActivity(eventId, { page, limit: 20, status });
      setRows(res.data || []);
      setLastPage(res.pagination?.total_pages || 1);
      setTotal(res.pagination?.total || 0);
    } catch (err) {
      setError(err.message || "Could not load task activity");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, page, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-none">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setStatus(tab.key);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                status === tab.key ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <button
          onClick={load}
          disabled={loading}
          title="Reload"
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
        >
          <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="px-4 py-2.5 font-medium">User</th>
              <th className="px-4 py-2.5 font-medium">Task</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium text-right">Points</th>
              <th className="px-4 py-2.5 font-medium">Started</th>
              <th className="px-4 py-2.5 font-medium">Completed</th>
              <th className="px-4 py-2.5 font-medium" title="From opening the task to completing it">
                Duration
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            )}

            {!loading && error && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center">
                  <FiAlertCircle className="mx-auto mb-2 text-rose-400" size={20} />
                  <p className="text-gray-600">{error}</p>
                </td>
              </tr>
            )}

            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  {status ? `No ${status} tasks for this event yet.` : "No task activity for this event yet."}
                </td>
              </tr>
            )}

            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50/70 transition border-b border-gray-50">
                <td className="px-4 py-2.5">
                  <p className="font-medium text-gray-700">{r.username || "—"}</p>
                  <p className="text-[10px] text-gray-400">{r.user_email}</p>
                </td>
                <td className="px-4 py-2.5 text-gray-700">{r.task_title || `#${r.task_id}`}</td>
                <td className="px-4 py-2.5">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                    {TASK_TYPE_LABEL[r.task_type] || r.task_type}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${
                      r.status === "completed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
                <td
                  className={`px-4 py-2.5 text-right tabular-nums font-semibold ${
                    r.reward_points > 0 ? "text-gray-800" : "text-gray-300"
                  }`}
                >
                  {r.reward_points > 0 ? `+${r.reward_points}` : "—"}
                </td>
                <td className="px-4 py-2.5">
                  <TimeCell value={r.visited_at} />
                </td>
                <td className="px-4 py-2.5">
                  <TimeCell value={r.completed_at} empty={r.status === "started" ? "Not finished" : "—"} />
                </td>
                <td className="px-4 py-2.5 text-gray-700 font-medium tabular-nums whitespace-nowrap">
                  {formatDuration(r.duration_seconds)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {lastPage > 1 && (
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
          <span>
            Showing {rows.length} of {total} rows
          </span>
          <div className="flex items-center gap-1.5">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Previous
            </button>
            <span className="px-2 font-medium">
              Page {page} of {lastPage}
            </span>
            <button
              disabled={page >= lastPage || loading}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <p className="px-4 py-3 border-t border-gray-100 text-[11px] text-gray-400 leading-relaxed">
        "Started" is a user who opened this task's link and never came back to finish it — no points are
        credited until it moves to "Completed".
      </p>
    </div>
  );
};

export default TaskActivityTab;
